import { MembershipStatusEnum } from '../../molecules/StatusIndication/MembershipStatusIndication/types';
import { PaymentMethodTypeEnum } from '../../types/entities/payment';
import { Pricify } from '../price';
import { ProductPaymentPlanTypeEnum } from '../constants';
import dayjs from 'dayjs';
import { DiscountModalTabEnum, TDiscountModalVariant } from '../../organisms/discount-modal/types';
import { PaymentStatusEnum, EInvoiceVariant } from '../../organisms/invoiceTemplate/types';
import { ESports } from '../../types';
import { ProgramTypesEnum } from '@bondsports/types';

function daysUntilRetryMapper(daysUntilRetry: number): string {
	const TODAY = 0;
	const TOMORROW = 1;
	switch (daysUntilRetry) {
		case TODAY:
			return `The scheduled payment will be retried today.`;
		case TOMORROW:
			return `The scheduled payment will be retried tomorrow.`;
		default:
			return `The scheduled payment will be retried in ${daysUntilRetry} days.`;
	}
}

function dayOfMonthMapper(day: number): string {
	if (!day || day < 1 || day > 31) {
		return '';
	}

	if (day > 10 && day < 20) {
		return `${day}th`;
	}

	const reminder = day % 10;

	switch (reminder) {
		case 1:
			return `${day}st`;
		case 2:
			return `${day}nd`;
		case 3:
			return `${day}rd`;
		default:
			return `${day}th`;
	}
}

function startsInMonthsMapper(month?: number): string {
	if (!month) {
		return 'immediately';
	}

	if (month === 1) {
		return '1 month';
	}

	return `${month} months`;
}

export const priceTypeTranslations = {
	group_rental: 'Group',
	per_participant_rental: 'Participant',
	rental: 'Rental',
};

export const DiscountModalVariantMap: Record<TDiscountModalVariant, string> = {
	invoice: 'Invoice',
	cart: 'Cart',
};

export const en = {
	PaymentMethodTypeEnum: {
		[PaymentMethodTypeEnum.BALANCE]: 'Credit Balance',
		[PaymentMethodTypeEnum.CASH]: 'Cash',
		[PaymentMethodTypeEnum.CARD]: 'Credit Card',
		[PaymentMethodTypeEnum.ACH]: 'ACH',
		[PaymentMethodTypeEnum.CHECK]: 'Check',
		[PaymentMethodTypeEnum.OTHER]: 'Other',
		[PaymentMethodTypeEnum.MIGRATED]: 'Migrated',
		[PaymentMethodTypeEnum.CARD_ON_TERMINAL]: 'Card Terminal',
		swipe: 'Swipe',
		[PaymentMethodTypeEnum.GIFT_CARD]: 'Gift Card',
		[PaymentMethodTypeEnum.DISCOUNT]: 'Discount',
		[PaymentMethodTypeEnum.REVERT_DISCOUNT]: 'Revert Discount',
		[PaymentMethodTypeEnum.NOT_SET]: 'Not Set',
		unknown: 'Unknown',
	},
	refundPupop: {
		paymentMethodPicker: { achIfo: 'If the bank initiates a dispute, that may result in two refund transactions.' },
	},
	ShowMoreButton: { showMore: 'Show more', showLess: 'Show less' },
	attendancePopup: {
		attendeeCount: (count: number) => `${count} attendee${count === 1 ? '' : 's'}`,
		checkedIn: 'Checked in',
		missingPayments: 'Missing payments:',
		publicNotes: 'Public notes',
		privateNotes: 'Private notes',
		viewConflict: 'View conflict',
	},
	buttons: { back: 'Back' },
	invoice: {
		toolTips: {
			paidInAdvance: (val: string) => `Paid in advance on ${val}.`,
			paidOn: (val: string) => `Paid on ${val}`,
			paid: 'Paid',
			after: 'after',
			failedAttemps: (failedCount?: number) => (!!failedCount ? `${failedCount} failed attempts.` : ''),
			failedAttempsAfterPayment: (content?: string, paid?: string, after?: string, failedContent?: string) =>
				`${content || paid} ${after} ${failedContent}`,
			lastFailed: (lastFailedAt: string) => `Last failed attempt was on ${lastFailedAt}`,
			error: (ErrorMessage: string) => (!!ErrorMessage ? `due to: ${ErrorMessage}` : ''),
			nextRetry: (daysUntilRetry: number) => daysUntilRetryMapper(daysUntilRetry),
			combined: 'This installment is now combined with the next one, totaling',
			paymentMethodUpdated: 'NOTICE: The payment method has been updated.',
		},
		dispute: {
			fallbackReason: (code: string) =>
				code.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()),
			fallbackStage: (code: string) =>
				code.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()),
			reasonPrefix: 'Reason',
			amountLabel: (amount: number) => `Disputed amount: ${Pricify(amount)}`,
			created: (date: string) => `Created on ${date}`,
			updated: (date: string) => `Updated on ${date}`,
			stages: {
				needs_response: 'Needs response',
				warning_needs_response: 'Warning — needs response',
				under_review: 'Under review',
				warning_under_review: 'Warning — under review',
				won: 'Dispute won',
				lost: 'Dispute lost',
				charge_refunded: 'Charge refunded',
			},
			/** Stripe `Dispute.reason` values (and common variants). */
			reasons: {
				fraudulent: 'Fraudulent',
				duplicate: 'Duplicate',
				subscription_canceled: 'Subscription canceled',
				product_unacceptable: 'Product or service unacceptable',
				product_not_received: 'Product or service not received',
				unrecognized: 'Unrecognized',
				credit_not_processed: 'Credit not processed',
				general: 'General',
				debit_not_authorized: 'Debit not authorized',
				incorrect_account_details: 'Incorrect account details',
				insufficient_funds: 'Insufficient funds',
				bank_cannot_process: 'Bank cannot process',
				check_returned: 'Check returned',
				customer_initiated: 'Customer initiated',
			},
		},
		header: {
			title: 'Invoice no.',
			[EInvoiceVariant.PAYMENT_PRINT]: 'Receipt no.',
			[EInvoiceVariant.PRINT]: 'Invoice no.',
			[EInvoiceVariant.PAYMENT]: 'Receipt no.',
		},
		billingInfo: {
			title: 'Billing Info',
			purchaseDate: 'Purchase date',
			dueDate: 'Due date',
			customer: 'Customer',
			transactionFee: (totalFee?: number, totalTips?: number) => 
				totalFee || totalTips
					? `(includes a ${Pricify(totalFee ?? 0, undefined, undefined, true)} fee${totalTips ? ` + ${Pricify(totalTips, undefined, undefined, true)} tip` : ''})`
					: '(Transaction fee may apply)',
			total: (isPayment?: boolean) => (isPayment ? 'Amount paid' : 'Total'),
		},
		details: {
			voidToggle: 'Voided Items',
			title: (isPayment?: boolean) => (isPayment ? 'Invoice details' : 'Details'),
			summary: {
				subtotal: 'Subtotal',
				total: (isPayment?: boolean) => (isPayment ? 'Invoice total' : 'Total'),
				tax: 'Tax',
				taxInc: 'Tax Inc.',
				discounts: 'Discounts',
				fees: 'Fees',
				tip: 'Tip',
			},
			table: {
				columns: {
					item: 'Purchased items',
					quantity: 'Qty',
					price: 'Unit price',
					tax: 'Tax',
					total: 'Total',
				},
			},
		},
		paymentMethods: {
			'gift-card': 'Gift Card',
			check: 'Check',
			balance: 'Balance',
		},
		paymentsSection: {
			title: 'Payments',
			payment: 'Payment',
			table: {
				columns: {
					customerName: 'Customer Name',
					id: 'Receipt ID',
					amount: 'Amount',
					total: 'Total',
					method: 'Method',
					date: 'Date',
					extras: 'Extras',
					fees: 'Fees',
					tip: 'Tip',
					status: 'Status',
				},
			},
			thankYou: 'THANK YOU',
			summary: {
				balanceDue: 'Balance Due',
				fees: 'Fees',
				tips: 'Tips',
				amountPaid: 'Amount Paid',
				totalPaid: 'Total Paid',
			},
			fees: {
				fixed: 'Fixed',
				tip: 'Tip',
			},
		},
		scheduledSection: {
			title: 'PAYMENTS SCHEDULE',
			table: {
				columns: {
					date: 'Date',
					amount: 'Amount',
					status: 'Status',
					fees: 'Fees',
					method: 'Method',
					total: 'Total Price',
				},
			},
		},
		notes: {
			title: 'Notes',
		},
		payments: {
			paid: 'Paid',
			partial: 'Partially paid',
			not_paid: 'Unpaid',
			pending: 'Pending',
			refunded: 'Refunded',
			void: 'Voided',
			scheduled: 'Scheduled Payments',
			disputed: 'Disputed',
		},
		appendix: {
			title: 'Schedule',
			scheduleTab: {
				columns: {
					description: 'Description',
					addons: 'Add Ons',
					dayTime: 'Day & Time',
					resource: 'Resource',
					event: 'Event Name',
				},
				info: {
					more: 'More...',
				},
				attendeeInfo: {
					participant: 'Participant',
					program: 'Program',
					products: 'Products',
					sessions: 'Sessions',
				},
			},
		},
	},
	public: 'Public',
	private: 'Private',
	paymentStatusTooltipMapper: {
		[PaymentStatusEnum.FULLY_PAID]: 'Paid',
		[PaymentStatusEnum.NOT_PAID]: 'Not Paid',
		[PaymentStatusEnum.PARTIAL_PAYMENT]: 'Partially Paid',
		[PaymentStatusEnum.REFUNDED]: 'Refunded',
		[PaymentStatusEnum.DISPUTED]: 'Disputed',
		[PaymentStatusEnum.CHARGEBACK]: 'Chargeback',
		[PaymentStatusEnum.VOID]: 'Void',
	},
	membershipStatusMapper: {
		[MembershipStatusEnum.ACTIVE]: 'Active',
		[MembershipStatusEnum.PAUSE_PENDING]: 'Pause Scheduled',
		[MembershipStatusEnum.PAUSED_CANCELLED]: 'Paused',
		[MembershipStatusEnum.ACTIVE_CANCELLED]: 'Active',
		[MembershipStatusEnum.CANCELLED]: 'Cancelled',
		[MembershipStatusEnum.EXPIRED]: 'Expired',
		[MembershipStatusEnum.PAUSED]: 'Paused',
		[MembershipStatusEnum.PENDING]: 'Pending',
	},
	membershipStatusToolTips: {
		[MembershipStatusEnum.PAUSE_PENDING]: `Active with Pause Scheduled`,
		[MembershipStatusEnum.PAUSED]: (endDate: string) => `Paused until ${endDate}`,
	},
	eventSlot: {
		join: 'Join',
		register: 'Register',
		programTypes: {
			[ProgramTypesEnum.LEAGUE]: 'League',
			[ProgramTypesEnum.TOURNAMENT]: 'Tournament',
			[ProgramTypesEnum.CLASS]: 'Class',
			[ProgramTypesEnum.CLINIC]: 'Clinic',
			[ProgramTypesEnum.CAMP]: 'Camp',
			[ProgramTypesEnum.LESSON]: 'Lesson',
			[ProgramTypesEnum.CLUB_TEAM]: 'Club',
			[ProgramTypesEnum.LEAGUE_V2]: 'League V2',
			[ProgramTypesEnum.TOURNAMENT_V2]: 'Tournament V2',
			[ProgramTypesEnum.CLUB_TEAM_V2]: 'Club Team V2',
		},
	},
	selcted: 'Selected',
	noResults: 'No Results',
	productCard: {
		buttons: {
			seeAll: 'See all',
			seeLess: 'See less',
			purchase: 'Purchase',
		},
		prices: {
			prorated: 'Prorated',
		},
		taxes: {
			title: 'Taxes',
			included: '(inc)',
		},
		addons: {
			title: 'Addons',
		},
		discounts: {
			title: 'Discounts',
		},
		registration: {
			title: 'Registration',
		},
		deposit: {
			title: 'Deposit',
			noDeposit: 'No deposit',
		},
		paymentPlan: {
			title: (isCanceled: boolean, type?: ProductPaymentPlanTypeEnum): string => {
				if (isCanceled) {
					return 'No payment plan';
				}

				switch (type) {
					case ProductPaymentPlanTypeEnum.CUSTOM:
						return 'Custom payment plan';
					case ProductPaymentPlanTypeEnum.ROLLING:
						return 'Rolling payment plan';
					case ProductPaymentPlanTypeEnum.PAYMENT_DATE:
						return 'Payment date plan';
					default:
						return 'No payment plan';
				}
			},
			rolling: {
				maxInstallments: (installments: number) =>
					installments ? `${installments} installments` : 'no set no. of installments',
				dayOfMonth: (dayOfMonth: number) =>
					dayOfMonth ? `every ${dayOfMonthMapper(dayOfMonth)} of the month` : 'no day set',
				startsImmediately: `starts immediately`,
				startsInMonths: (startsInMonths: number) => `starts after ${startsInMonthsMapper(startsInMonths)}`,
			},
			custom: {
				noSetDate: 'no set date',
			},
			paymentDate: {
				dueOn: (schedule?: any[]) =>
					`payment due on ${schedule?.length && dayjs(schedule[0].paymentDate).format('MMM DD, YYYY')}`,
			},
			inactive: 'Inactive',
		},
	},
	more: 'more',
	glCodes: {
		accountingCodes: 'Accounting Codes',
		noAccountingCodes:
			'There are no accounting codes in this program.\nYou can edit the basic info to add accounting codes.',
		other: 'other',
	},
	addDiscount: {
		lineItemMenu: 'Add discount to line item',
		amountInput: {
			inputLabel: 'Discount amount',
			currencySymbol: '$',
			subtotalBalanceAfterDiscount: (val?: number) =>
				`Subtotal balance after discount: ${Pricify(val, undefined, undefined, true)}`,
			invoiceSubtotalBalance: (val?: number) => `Line item subtotal: ${Pricify(val, undefined, undefined, true)}`,
			discountTotals: (amount: number, percent: number) =>
				`${percent}% (${Pricify(amount, undefined, undefined, true)})`,
		},
		header: {
			title: 'Add Discount',
			amount: (variant: TDiscountModalVariant, val: string) => `${DiscountModalVariantMap[variant]} subtotal: ${val}`,
			lineItemAmount: (val: string) => `Line item amount: ${val}`,
			subtotalBalance: (variant: TDiscountModalVariant, val: string) =>
				`${DiscountModalVariantMap[variant]} subtotal balance: ${val}`,
			tabs: {
				[DiscountModalTabEnum.PROMO_CODE]: 'Promo Code',
				[DiscountModalTabEnum.CUSTOM]: 'Custom',
			},
		},
		content: {
			custom: {
				discountError: (val: string, percent: string) => `Max discount ${val} or ${percent}%`,
				reasonSelect: {
					label: 'Select reason',
					placeholder: 'Search',
				},
				notes: {
					public: {
						openNote: 'Public note',
						closedNote: 'Add private note',
						tooltip: 'The public note is visible to the customer.',
						clear: 'Clear',
					},
					both: {
						private: 'Private note',
						public: 'Public note',
						privateTooltip: 'The private note is only visible to staff.',
						publicTooltip: 'The public note is visible to the customer.',
						clear: 'Clear',
					},
				},
			},
			promoCode: {
				alreadyApplied: 'A promo code has already been added.\nA custom discount can still be applied.',
				error: 'Error in loading promo codes. Please try again later',
				noValidCodes: 'There are currently no valid promo codes.',
				defaultStateMessage: 'Oops, Something went wrong.',
				select: { placeholder: 'search', label: 'Select promo code' },
			},
		},
		footer: {
			cancel: 'CANCEL',
			confirm: 'APPLY',
		},
	},
	fieldValidationErrors: {
		required: 'required',
		minLength: 'minimum length is',
	},
	applicationTemplate: {
		formTitle: 'RESPONSES',
		customerAgreementColumn: 'Agreed: ',
		agreed: 'Agreed',
		dateSigned: (dayLabel: string, timeLabel: string): string => `${dayLabel} at ${timeLabel}`,
		customerDetails: {
			title: 'PARTICIPANT',
			details: {
				dateSigned: 'Form completed',
				participantName: 'Participant',
				primary: 'Account admin',
				emailAddress: 'Email',
				participantEmail: 'Participant email',
				accountAdminEmail: 'Account admin email',
				phoneNumber: 'Phone',
				address: 'Address',
			},
		},
	},
	components: { select: { selectAll: 'Select All', error: 'Error loading' } },
	usePermissions: { noPermissions: 'No permissions found' },
	slotCard: {
		close: {
			ariaLabel: {
				close: `Close Slot Card Popup`,
			},
		},
		header: {
			reservation: `Reservation:`,
			conflicts: `Conflicts`,
			reservationHistory: 'Reservation history',
			viewHistory: 'View History',
			viewConflicts: 'View Conflicts',
			ariaLabel: {
				conflicts: 'View conflicts button',
				reservationId: 'Reservation link',
				reservationHistory: 'Reservation history link',
			},
			tags: {
				customer: 'Customer',
				punchPass: 'Punch pass',
				maintenance: 'Maintenance',
				internal: 'Internal',
				paid: 'Paid',
				unpaid: 'Unpaid',
				partiallyPaid: 'Partially Paid',
				refunded: 'Refunded',
				voided: 'Voided',
				hold: 'Hold',
				private: `Private`,
			},
			occurenceCount: (index, totalCount) => `${index}/${totalCount}`,
		},
		body: {
			duration: (duration: string) => `(${duration})`,
			viewMoreMaintenace: (
				title: string,
				maintenanceTiming: string,
				startDate: string,
				startTime: string,
				durationValue: string,
				durationUnit: string
			) => `${title} - ${maintenanceTiming} - ${startDate} - ${startTime} - (${durationValue} ${durationUnit})`,
			viewMoreButton: (count: string | number) => `view ${count} more`,
			maintenanceTiming: {
				AFTER: 'After',
				BEFORE: 'Before',
				AT_THE_BEGINING: 'At the Beginning',
				AT_THE_END: 'At the End',
			},
			durationUnitTypes: {
				weeks: 'w',
				minutes: 'm',
				hours: 'hr',
				days: 'd',
			},
			editNotes: 'Edit notes',
			addNotes: 'Add notes',
			viewForms: 'View forms',
		},
		footer: {
			deleteButton: `DELETE`,
			editButton: `Edit`,
			duplicateButton: `Duplicate`,
			ariaLabel: {
				deleteButton: `Delete button`,
				editButton: `Edit button`,
				duplicateButton: `Edit button`,
			},
		},
	},
	ESports: {
		[ESports.SOFTBALL]: 'Softball',
		[ESports.BASKETBALL]: 'Basketball',
		[ESports.FOOTBALL]: 'Football',
		[ESports.SOCCER]: 'Soccer',
		[ESports.BOWLING]: 'Bowling',
		[ESports.BOCCEBALL]: 'Bocceball',
		[ESports.CORNHOLE]: 'Cornhole',
		[ESports.DODGEBALL]: 'Dodgeball',
		[ESports.FRISBEE]: 'Frisbee',
		[ESports.HOCKEY]: 'Hockey',
		[ESports.KICKBALL]: 'Kickball',
		[ESports.LACROSSE]: 'Lacrosse',
		[ESports.PINGPONG]: 'Pingpong',
		[ESports.RUGBY]: 'Rugby',
		[ESports.SKEEBALL]: 'Skeeball',
		[ESports.TENNIS]: 'Tennis',
		[ESports.VOLLEYBALL]: 'Volleyball',
		[ESports.WIFFLEBALL]: 'Wiffleball',
		[ESports.BADMINTON]: 'Badminton',
		[ESports.FITNESS]: 'Fitness',
		[ESports.GOLF]: 'Golf',
		[ESports.PILATES]: 'Pilates',
		[ESports.RUNNING]: 'Running',
		[ESports.SKIING]: 'Skiing',
		[ESports.SNOWBOARDING]: 'Snowboarding',
		[ESports.YOGA]: 'Yoga',
		[ESports.BROOMBALL]: 'Broomball',
		[ESports.CRICKET]: 'Cricket',
		[ESports.CROSSFIT]: 'Crossfit',
		[ESports.CYCLING]: 'Cycling',
		[ESports.FIELD_HOCKEY]: 'Field Hockey',
		[ESports.RACQUETBALL]: 'Racquetball',
		[ESports.SPINNING]: 'Spinning',
		[ESports.SQUASH]: 'Squash',
		[ESports.SURFING]: 'Surfing',
		[ESports.SWIMMING]: 'Swimming',
		[ESports.WIND_SURFING]: 'Wind Surfing',
		[ESports.ADVENTURE]: 'Adventure',
		[ESports.BOXING]: 'Boxing',
		[ESports.BASEBALL]: 'Baseball',
		[ESports.DANCE]: 'Dance',
		[ESports.KICKBOXING]: 'Kickboxing',
		[ESports.MARTIAL_ARTS]: 'Martial Arts',
		[ESports.OUTDOORS]: 'Outdoors',
		[ESports.ROWING]: 'Rowing',
		[ESports.SAILING]: 'Sailing',
		[ESports.SUP]: 'Sup',
		[ESports.TRIATHLON]: 'Triathlon',
		[ESports.HANDBALL]: 'Handball',
		[ESports.CATCHBALL]: 'Catchball',
		[ESports.BLITZBALL]: 'Blitzball',
		[ESports.ROLLER_DERBY]: 'Roller Derby',
		[ESports.ICE_SKATING]: 'Ice Skating',
		[ESports.PICKLEBALL]: 'Pickleball',
		[ESports.AXE_THROWING]: 'Axe Throwing',
		[ESports.FUTSAL]: 'Futsal',
		[ESports.BIRTHDAY]: 'Birthday',
		[ESports.CORPORATE_EVENTS]: 'Corporate Events',
		[ESports.CURLING]: 'Curling',
		[ESports.GYMNASICS]: 'Gymnastics',
		[ESports.FIGURE_SKATING]: 'Figure Skating',
		[ESports.CHEER]: 'Cheer',
		[ESports.NINJA_PARKOUR]: 'Ninja & Parkour',
		[ESports.OTHER]: 'Other',
	},
	idHyperlink: {
		id: (id: string | number) => `#${id}`,
	},
	durationUnitTypes: {
		weeks: 'w',
		minutes: 'm',
		hours: 'hr',
		days: 'd',
	},
	overnightIndicator: (daysDifference: number) => ` +${daysDifference}`,
	clampOrTruncateText: {
		ariaLabel: {
			viewMore: 'View more text',
		},
	},
};
